import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { MDXRemote, MDXRemoteSerializeResult } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";
import remarkGfm from "remark-gfm";
import { ReactElement } from "react";
import Link from "next/link";
import { AuthorBox } from "~/components/blog/AuthorBox";
import { CheckItem, CheckList } from "~/components/blog/MdxChecklist";
import { MdxH2, MdxH3 } from "~/components/blog/MdxHeadings";
import { TableOfContents } from "~/components/blog/TableOfContents";
import ShareBar from "~/components/ShareBar";
import { MainLayout } from "~/components/layouts/MainLayout";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { BG, TEXT } from "~/components/theme/colors";
import { getAuthorByName, type AuthorProfile } from "~/lib/authors";
import type { City } from "~/lib/cities";
import { getFeaturedCitiesForService } from "~/lib/firms.server";
import {
  getAllPosts,
  getPostBySlug,
  getRecentPostsForService,
  type PostMeta,
} from "~/lib/mdx";
import {
  getCityServicePath,
  getRelatedServices,
  getServiceBySlug,
  type Service,
} from "~/lib/services";
import { extractHeadings, type Heading } from "~/lib/toc";
import { NextPageWithLayout } from "~/pages/_app";

type Props = {
  meta: PostMeta;
  authorProfile: AuthorProfile | null;
  headings: Heading[];
  mdxSource: MDXRemoteSerializeResult;
  recentPosts: PostMeta[];
  primaryService: Service | null;
  relatedServices: Service[];
  featuredCities: City[];
};

const MdxTable = ({ children }: { children?: React.ReactNode }) => (
  <div className="mdx-table my-6">
    <div className="overflow-hidden rounded-[1.25rem] border border-slate-200/80 bg-white shadow-[0_24px_60px_-36px_rgba(2,132,199,0.35)] ring-1 ring-sky-100/80">
      <div className="border-b border-sky-100 bg-sky-50/80 px-4 py-2 text-[0.72rem] font-medium text-sky-700 md:hidden">
        Jeśli tabela wychodzi poza ekran, przesuń ją w bok.
      </div>
      <div
        className="mdx-table__scroll relative overflow-x-auto overscroll-x-contain"
        role="region"
        aria-label="Tabela w artykule"
      >
        <div className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-8 bg-gradient-to-l from-white via-white/90 to-transparent md:hidden" />
        <table className="min-w-full">{children}</table>
      </div>
    </div>
  </div>
);

const mdxComponents = {
  h2: MdxH2,
  h3: MdxH3,
  table: MdxTable,
  CheckList,
  CheckItem,
};

const BlogPostPage: NextPageWithLayout<Props> = ({
  meta,
  authorProfile,
  headings,
  mdxSource,
  recentPosts,
  primaryService,
  relatedServices,
  featuredCities,
}) => {
  const baseUrl =
    (process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl").replace(
      /\/+$/,
      ""
    );
  const toAbsoluteUrl = (value: string | null) => {
    if (!value) {
      return null;
    }

    if (/^https?:\/\//.test(value)) {
      return value;
    }

    return `${baseUrl}${value.startsWith("/") ? value : `/${value}`}`;
  };
  const articleUrl = `${baseUrl}/poradnik/${meta.slug}/`;
  const authorHref = authorProfile ? `/autorzy/${authorProfile.slug}/` : null;
  const authorUrl = authorHref ? `${baseUrl}${authorHref}` : null;
  const authorImageUrl = toAbsoluteUrl(authorProfile?.image ?? null);
  const authorSchema = authorProfile
    ? {
        "@type": "Person",
        "@id": `${authorUrl}#main-author`,
        identifier: authorProfile.slug,
        name: authorProfile.name,
        url: authorUrl,
        jobTitle: authorProfile.role,
        description: authorProfile.shortBio,
        ...(authorProfile.sameAs.length > 0 ? { sameAs: authorProfile.sameAs } : {}),
        ...(authorImageUrl ? { image: authorImageUrl } : {}),
      }
    : {
        "@type": "Person",
        name: meta.author,
      };
  const formattedDate = new Date(meta.date).toLocaleString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const formattedUpdatedAt = new Date(meta.updatedAt).toLocaleString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: meta.title,
            description: meta.excerpt,
            url: articleUrl,
            mainEntityOfPage: articleUrl,
            datePublished: meta.date,
            dateModified: meta.updatedAt,
            author: authorSchema,
            publisher: {
              "@id": `${baseUrl}/#organization`,
            },
            articleSection: meta.category,
            inLanguage: "pl-PL",
          }),
        }}
      />

      <div className={`${BG.lightMuted} px-4 py-12 sm:py-16 lg:px-6`}>
        <div className="mx-auto max-w-3xl">
          <span className="mb-4 inline-block rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-800">
            {meta.category}
          </span>
          <h1 className={`text-3xl font-bold ${TEXT.primary} mb-4`}>
            {meta.title}
          </h1>
          <div className="flex flex-col gap-2 text-sm text-gray-500 sm:flex-row sm:items-center">
            {meta.author &&
              (authorHref ? (
                <Link href={authorHref} className="hover:text-sky-800">
                  Autor: {meta.author}
                </Link>
              ) : (
                <span>Autor: {meta.author}</span>
              ))}
            <div className="sm:ml-auto sm:text-right">
              <time className="block" dateTime={meta.date}>
                Opublikowano: {formattedDate}
              </time>
              {meta.updatedAt !== meta.date && (
                <time className="mt-1 block" dateTime={meta.updatedAt}>
                  Aktualizacja: {formattedUpdatedAt}
                </time>
              )}
            </div>
          </div>
        </div>
      </div>

      <MarginLayout className="mx-auto max-w-3xl py-12">
        <div className="mb-8 flex items-start gap-3">
          <div className="flex-1 [&>nav]:mb-0">
            <TableOfContents headings={headings} />
          </div>
          <ShareBar
            url={articleUrl}
            title={meta.title}
            description={meta.excerpt}
            platforms={["x", "linkedin", "facebook", "email", "copy"]}
            variant="compact"
            iconSize={24}
            showNativeShare={false}
          />
        </div>

        <article className="blog-prose prose prose-sky max-w-none prose-headings:text-sky-800 prose-a:text-sky-700">
          <MDXRemote {...mdxSource} components={mdxComponents} />
        </article>

        {authorProfile && <AuthorBox author={authorProfile} />}


        {primaryService && (
          <aside className="mt-6 rounded-xl border border-gray-200 bg-white px-6 py-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Powiązane usługi
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              Ten wpis najmocniej wspiera usługę{" "}
              <Link
                href={primaryService.href}
                className="font-medium text-sky-700 underline underline-offset-2 hover:text-sky-900"
              >
                {primaryService.name}
              </Link>
              . Poniżej znajdziesz też powiązane landingi z tej samej grupy tematycznej.
            </p>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <Link
                href={primaryService.href}
                className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-4 transition-colors hover:border-sky-300 hover:bg-sky-100"
              >
                <span className="block text-sm font-semibold text-sky-800">
                  Główna usługa
                </span>
                <span className="mt-1 block text-base font-medium text-gray-900">
                  {primaryService.name}
                </span>
              </Link>

              {relatedServices.map((service) => (
                <Link
                  key={service.slug}
                  href={service.href}
                  className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 transition-colors hover:border-sky-300 hover:bg-sky-50"
                >
                  <span className="block text-sm font-semibold text-gray-900">
                    {service.name}
                  </span>
                  <span className="mt-1 block text-sm text-gray-500">
                    Pokrewna usługa z tej samej kategorii
                  </span>
                </Link>
              ))}
            </div>

            {primaryService.cityPages && featuredCities.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Wybrane miasta dla tej usługi
                </h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {featuredCities.map((city) => (
                    <Link
                      key={city.slug}
                      href={getCityServicePath(city.slug, primaryService.slug)}
                      className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-800 transition-colors hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
                    >
                      {primaryService.shortName} {city.nameLocative}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        )}

        {recentPosts.length > 0 && (
          <section className="mt-16 border-t border-gray-200 pt-10">
            <h2 className="mb-6 text-lg font-semibold text-gray-900">
              Przeczytaj również
            </h2>
            <ul className="flex flex-col gap-4">
              {recentPosts.map((post) => (
                <li key={post.slug}>
                  <Link
                    href={`/poradnik/${post.slug}`}
                    className="group flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md"
                  >
                    <span className="inline-block self-start rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                      {post.category}
                    </span>
                    <span className="font-medium text-gray-900 group-hover:text-sky-800">
                      {post.title}
                    </span>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {post.excerpt}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </MarginLayout>
    </>
  );
};

BlogPostPage.getLayout = (page: ReactElement) => {
  const props = (page as React.ReactElement<Props>).props;
  return (
    <MainLayout>
      <NextSeo
        title={props.meta?.title ?? "Blog"}
        description={props.meta?.excerpt}
      />
      {page}
    </MainLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = getAllPosts();
  return {
    paths: posts.map((p) => ({ params: { slug: p.slug } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const slug = params?.slug as string;
  const post = getPostBySlug(slug);
  const authorProfile = getAuthorByName(post.author);
  const headings = extractHeadings(post.content);
  const primaryService = post.service ? getServiceBySlug(post.service) ?? null : null;
  const relatedServices = primaryService
    ? getRelatedServices(primaryService.slug).slice(0, 2)
    : [];
  const featuredCities =
    primaryService?.cityPages
      ? getFeaturedCitiesForService(primaryService.slug, {
          priorities: ["P0", "P1"],
          limit: 6,
        })
      : [];
  const mdxSource = await serialize(post.content, {
    mdxOptions: { remarkPlugins: [remarkGfm] },
  });
  const recentPosts = getRecentPostsForService(post.service, {
    excludeSlug: slug,
    limit: 3,
  });

  return {
    props: {
      meta: {
        slug: post.slug,
        title: post.title,
        date: post.date,
        updatedAt: post.updatedAt,
        excerpt: post.excerpt,
        category: post.category,
        coverImage: post.coverImage,
        author: post.author,
        service: post.service,
      },
      authorProfile,
      headings,
      mdxSource,
      recentPosts,
      primaryService,
      relatedServices,
      featuredCities,
    },
  };
};

export default BlogPostPage;
